import React, { useEffect, useState } from 'react';
import { useGetInternalUsersQuery, useUpdateCampaignMutation } from '../endpoints/accounts.endpoints';
import { Campaign } from '../types/client-management.types';
import { CircleAlert, CircleCheck, CircleDollarSign, CircleOff, ReceiptText } from 'lucide-react';
import CompanySelector from './company-selector';
import { Company } from 'src/pages/billing/types/billing.types';
import SkuSelector from './sku-selector';
import { useGetSKUsQuery } from 'src/redux/billing/billing.endpoints';
import UserSelector from './user-selector';

interface CampaignTableProps {
  campaigns: Campaign[];
  companies: Company[];
  filter?: 'attention_needed' | 'all';
  className?: string;
  searchQuery: string;
  onCompanySelect?: (accountId: string, companyId: string) => void;
  onDoNotBillToggle?: (campaignId: string, doNotBill: boolean) => void;
  onPreBilledToggle?: (campaignId: string, preBilled: boolean) => void;
  onSkuSelect?: (campaignId: string, skuId: string) => void;
  onUserSelect?: (campaignId: string, userId: string) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = ({
  className,
  filter = 'all',
  campaigns,
  companies,
  searchQuery,
  onCompanySelect,
  onDoNotBillToggle,
  onPreBilledToggle,
  onSkuSelect,
  onUserSelect,
}) => {
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);

  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  const { data: skus = [] } = useGetSKUsQuery();
  const { data: users = [] } = useGetInternalUsersQuery();

  useEffect(() => {
    setFilteredCampaigns(campaigns.filter((campaign: Campaign) => {
      if (filter === 'attention_needed') {
        if (searchQuery.trim()) {
          return campaign.attention_needed && (campaign?.title?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign?.account?.source?.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return campaign.attention_needed;
      }

      if (searchQuery.trim()) {
        return campaign?.title?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign?.account?.source?.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    }));
  }, [filter, campaigns, searchQuery]);

  const handleCompanySelect = async(campaignId: string, companyId: string) => {
    try {
      await updateCampaign({
        campaignId,
        company: companyId || null,
      }).unwrap();
      onCompanySelect?.(campaignId, companyId);
    } catch (error) {
      console.error('Failed to update company:', error);
    }
  };

  const handleDoNotBillToggle = async(campaign: Campaign) => {
    try {
      await updateCampaign({
        campaignId: campaign.id,
        do_not_bill: !campaign.do_not_bill,
      }).unwrap();
      onDoNotBillToggle?.(campaign.id, !campaign.do_not_bill);
    } catch (error) {
      console.error('Failed to update do not bill:', error);
    }
  };

  const handlePreBilledToggle = async(campaign: Campaign) => {
    try {
      await updateCampaign({
        campaignId: campaign.id,
        pre_billed: !campaign.pre_billed,
      }).unwrap();
      onPreBilledToggle?.(campaign.id, !campaign.pre_billed);
    } catch (error) {
      console.error('Failed to update pre billed:', error);
    }
  };

  const handleSkuSelect = async(campaignId: string, skuId: string) => {
    try {
      await updateCampaign({
        campaignId,
        sku: skuId || null,
      }).unwrap();
      onSkuSelect?.(campaignId, skuId);
    } catch (error) {
      console.error('Failed to update sku:', error);
    }
  };

  const handleUserSelect = async(campaignId: string, userId: string) => {
    try {
      await updateCampaign({
        campaignId,
        owner: userId || null,
      }).unwrap();
      onUserSelect?.(campaignId, userId);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  return (
    <div className={`overflow-y-scroll ${className}`}>

      <div className='flex flex-row flex-wrap gap-4'>
        {filteredCampaigns.map((campaign: Campaign) => (
          <div
            className='bg-white flex flex-col justify-between rounded-lg p-4 w-fit relative'
            key={campaign.id}
          >
            {campaign.attention_needed && (
              <div className='absolute top-2 right-2'>
                <button className='text-red-500 bg-white rounded-full opacity-80'>
                  <CircleAlert className='size-4' />
                </button>
              </div>
            )}
            <div className='text-lg font-normal'>
              {campaign.title}
            </div>
            <div className='flex flex-row gap-2 flex-wrap justify-between'>
              <div className='text-xs text-gray-500'>
                {campaign.account?.source?.toLocaleLowerCase().replace('_', ' ')}
              </div>
              <div className='flex flex-row gap-2'>
                <div
                  onClick={() => handlePreBilledToggle(campaign)}
                  className='flex flex-row gap-1 items-center text-xs text-gray-500 bg-gray-200 hover:bg-gray-201 rounded-full px-2 py-1 cursor-pointer'
                >
                  {campaign.pre_billed ? (
                    <ReceiptText className='size-3 text-green-501' />
                  ) : (
                    <CircleDollarSign className='size-3 text-red-500' />
                  )}
                  {campaign.pre_billed ? 'Pre-Billed' : 'Not Pre-Billed'}
                </div>
                <div
                  onClick={() => handleDoNotBillToggle(campaign)}
                  className='flex flex-row gap-1 items-center text-xs text-gray-500 bg-gray-200 hover:bg-gray-201 rounded-full px-2 py-1 cursor-pointer'
                >
                  {campaign.do_not_bill ? (
                    <CircleOff className='size-3 text-red-500' />
                  ) : (
                    <CircleCheck className='size-3 text-green-501' />
                  )}
                  {campaign.do_not_bill ? 'Do Not Bill' : 'Bill'}
                </div>
              </div>

            </div>
            <div className='text-xs text-gray-400'>
              Account:
              {' '}
              {campaign.account.title}
            </div>
            <div className='flex gap-2'>
              <CompanySelector
                attentionNeeded={campaign.attention_needed}
                campaign={campaign}
                accountId={campaign.account.id}
                companies={companies}
                companyId={campaign.company || ''}
                accountName={campaign.title}
                campaignName={campaign.title}
                onCompanySelect={(companyId) => handleCompanySelect(campaign.id, companyId)}
              />

              <SkuSelector
                campaign={campaign}
                skus={skus}
                onSkuSelect={(skuId) => handleSkuSelect(campaign.id, skuId)}
              />
            </div>
            <div>
              <UserSelector
                campaign={campaign}
                users={users}
                onUserSelect={(userId) => handleUserSelect(campaign.id, userId)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignTable;
